import { createPortal } from 'react-dom';
import { useUi } from '../../stores/ui.js';
import { cn } from '../../lib/format.js';

/**
 * Toasts confirm that something happened. They matter more here than in a shop:
 * an operator running a bulk action needs to know the sixty rows actually moved,
 * and how many did not.
 */

const tones = {
  default: 'border-mist bg-surface',
  success: 'border-success/30 bg-success/8',
  danger: 'border-danger/30 bg-danger/8',
};

const portalTarget = () => (typeof document === 'undefined' ? null : document.body);

export const Toaster = () => {
  const toasts = useUi((state) => state.toasts);
  const dismiss = useUi((state) => state.dismissToast);
  const target = portalTarget();

  if (!target) return null;

  return createPortal(
    <div
      role="region"
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed right-0 bottom-0 z-[60] flex flex-col items-end gap-2 p-4"
    >
      {toasts.map((entry) => (
        <div
          key={entry.id}
          className={cn(
            'pointer-events-auto flex w-full max-w-sm items-start gap-3 border px-3.5 py-2.5',
            'shadow-[0_12px_32px_-12px_rgba(20,23,26,0.25)]',
            tones[entry.tone] ?? tones.default,
          )}
        >
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-ink">{entry.title}</p>
            {entry.description && <p className="mt-0.5 text-2xs text-muted">{entry.description}</p>}
            {entry.action && (
              <button
                type="button"
                onClick={() => {
                  entry.action.onClick?.();
                  dismiss(entry.id);
                }}
                className="mt-1.5 text-2xs font-medium text-ink underline decoration-mist underline-offset-2 hover:decoration-ink"
              >
                {entry.action.label}
              </button>
            )}
          </div>

          <button
            type="button"
            onClick={() => dismiss(entry.id)}
            aria-label="Dismiss"
            className="-mt-0.5 grid size-5 shrink-0 place-items-center rounded text-muted hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-signal"
          >
            <svg viewBox="0 0 16 16" className="size-2.5" fill="currentColor" aria-hidden="true">
              <path d="M4.3 3.24 8 6.94l3.7-3.7 1.06 1.06-3.7 3.7 3.7 3.7-1.06 1.06-3.7-3.7-3.7 3.7-1.06-1.06 3.7-3.7-3.7-3.7z" />
            </svg>
          </button>
        </div>
      ))}
    </div>,
    target,
  );
};

export default Toaster;
